import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { useAuthContext } from '@/shared/context/useAuthContext';
import { useLocation } from '@/shared/hooks/useLocation';
import { useCanSyncIntegrations } from '@/shared/hooks/useCanSyncIntegrations';
import { GoogleAdsCard, MetaAdsCard } from '../components/AdAccountCards';
import { AdBudgetCard } from '../components/paid/AdBudgetCard';
import { BrandTermsCard } from '../components/paid/BrandTermsCard';
import { AuctionInsightsCard } from '../components/paid/AuctionInsightsCard';
import { ChangeRulesCard } from '../components/paid/ChangeRulesCard';
import styles from './SettingsPage.module.css';
import own from './PaidSettingsPage.module.css';

const CONNECT_RESULTS: Record<string, { ok: boolean; message: string }> = {
  google_ads: { ok: true, message: 'Google Ads connected successfully' },
  google_ads_denied: { ok: false, message: 'Google Ads authorization was cancelled' },
  google_ads_callback_failed: {
    ok: false,
    message: 'Google Ads connection failed — please try again',
  },
  meta_ads: { ok: true, message: 'Meta Ads connected successfully' },
  meta_ads_denied: { ok: false, message: 'Meta Ads authorization was cancelled' },
  meta_ads_callback_failed: { ok: false, message: 'Meta Ads connection failed — please try again' },
};

/**
 * Settings > Paid: the settings of everything under Paid, one sub-section per area. Ad campaigns holds one set of
 * settings for Ad Campaigns and Jenny: the ad accounts, the approved budget, brand terms, auction insights and the
 * rules Jenny recommends changes by.
 */
export function PaidSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { impersonatedPractice } = useImpersonation();
  const { user } = useAuthContext();
  const { currentLocation } = useLocation();
  const canSync = useCanSyncIntegrations();
  const practiceId = impersonatedPractice?.id ?? user?.practiceId ?? '';
  const locationId = currentLocation?.id ?? '';
  const consumedRef = useRef(false);

  // Google Ads' connect flow comes back here with ?connected= or ?error=.
  useEffect(() => {
    if (consumedRef.current) return;
    const key = searchParams.get('connected') ?? searchParams.get('error');
    const result = key ? CONNECT_RESULTS[key] : undefined;
    if (!result) return;
    consumedRef.current = true;
    if (result.ok) toast.success(result.message);
    else toast.error(result.message);
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    next.delete('connected');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  return (
    <div className={styles.page}>
      <PageHeader title="Paid Settings" subtitle="Settings for everything under Paid." />
      <section className={own.section} aria-labelledby="paid-ad-campaigns">
        <h2 id="paid-ad-campaigns" className={own.sectionTitle}>
          Ad Campaigns
        </h2>
        <div className={styles.integrationsList}>
          <GoogleAdsCard practiceId={practiceId} canSync={canSync} />
          <MetaAdsCard practiceId={practiceId} canSync={canSync} />
        </div>
        {locationId && (
          <>
            <AdBudgetCard locationId={locationId} />
            <BrandTermsCard locationId={locationId} />
            <AuctionInsightsCard locationId={locationId} />
            <ChangeRulesCard locationId={locationId} unlock="typed" />
          </>
        )}
      </section>
    </div>
  );
}
